import type { ViewHook } from 'phoenix_live_view'
import { isArray, isPlainObject, isString } from 'remeda'
import { type Cleanup, composeCleanup } from '../helpers/cleanup'
import { isDraftInputElement, setupDraftInput } from '../helpers/draft-input'
import { setupPageComponent } from '../helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type BrandColorsHookContext = Pick<
  ViewHook,
  'el' | 'pushEvent' | 'handleEvent'
> & {
  // Phoenix owns the hook instance and its cleanup resource.
  _cleanup?: Cleanup
}

// Constants -------------------------------------------------------------------

const OPAQUE_HEX_COLOR_PATTERN = /^#[\da-f]{6}$/i

// Private functions -----------------------------------------------------------

// Applies the generated scale once; palette cells derive their colors from it.
const applyBrandColorScale = (colors: readonly string[]): void => {
  const metaThemeEl = document.querySelector('meta[name="theme-color"]')

  if (metaThemeEl && colors[1]) {
    metaThemeEl.setAttribute('content', `rgb(${colors[1]})`)
  }

  colors.forEach((color, index) => {
    document.documentElement.style.setProperty(
      `--color-brand-rgb-${index}`,
      color
    )
  })
}

// Keeps native form submission and live preview connected to the same value.
const setupBrandColorInput = (
  element: HTMLElement,
  hook: BrandColorsHookContext
): Cleanup => {
  if (!isDraftInputElement(element)) {
    return () => undefined
  }

  // Sends complete opaque hex colors for the server preview.
  const handleInput = (event: Event): void => {
    if (
      event.target === element &&
      isString(element.value) &&
      OPAQUE_HEX_COLOR_PATTERN.test(element.value)
    ) {
      hook.pushEvent('color_updated', { brand_color_hex: element.value })
    }
  }
  const cleanupDraft = setupDraftInput(element)

  element.addEventListener('input', handleInput)

  return composeCleanup(cleanupDraft, () => {
    element.removeEventListener('input', handleInput)
  })
}

// Public hook -----------------------------------------------------------------

export const BrandColors = {
  // Connects the upgraded picker and the server-generated palette preview.
  mounted(this: BrandColorsHookContext): void {
    this.handleEvent('colors_generated', (payload: unknown) => {
      if (
        isPlainObject(payload) &&
        isArray(payload.colors) &&
        payload.colors.every(isString)
      ) {
        applyBrandColorScale(payload.colors)
      }
    })

    this._cleanup = setupPageComponent(this.el, (element) =>
      setupBrandColorInput(element, this)
    )
  },

  // Releases form listeners and cancels initialization after navigation.
  destroyed(this: BrandColorsHookContext): void {
    this._cleanup?.()
    this._cleanup = undefined
  }
}
